import { Modal } from "@mantine/core";
import { useStorage } from "../../../hooks/useStorage";
import { useToast } from "../../../mantine/notifications/useToast";
import type { BaseWizardProps } from "../../../types/wizard.types";
import type { Storage } from "../../../utils/backend-types";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { BatteryStorageModalForm } from "./BatteryStorageModalForm";
import { useStorageMutation } from "./hooks/useStorageMutation";

type BatteryStorageModalProps = BaseWizardProps & {
  siteId: number;
  variantId: number;
};

function BatteryStorageModal({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  variantId,
  onClose,
  ...modeProps
}: BatteryStorageModalProps) {
  const { showToast } = useToast();
  const storageId = modeProps.mode === "edit" ? modeProps.objectId : undefined;
  const {
    data: storage,
    isLoading: isStorageLoading,
    error: storageError
  } = useStorage(storageId);
  const { createStorage, editStorage } = useStorageMutation(siteId);

  async function handleSubmit(data: Storage) {
    if (modeProps.mode === "create") {
      const enrichedData = {
        ...data,
        site: siteId,
        sketchElement: {
          xPosition: graphCoordinates.x,
          yPosition: graphCoordinates.y
        }
      };
      const created = await createStorage.mutateAsync({
        siteId,
        newStorage: enrichedData
      });
      showToast({
        type: "success",
        message: `Der Batteriespeicher ${created.name} wurde erstellt.`
      });
      onClose();
      return created;
    }

    const edited = await editStorage.mutateAsync({
      ...data,
      id: modeProps.objectId,
      site: siteId
    });
    showToast({
      type: "success",
      message: `Die Änderungen am Batteriespeicher ${edited.name} wurden gespeichert.`
    });
    onClose();
    return edited;
  }

  const title =
    modeProps.mode === "create"
      ? "Batteriespeicher erstellen"
      : `${storage?.name || "Batteriespeicher"} bearbeiten`;

  return (
    <Modal
      className="BatteryStorageModal"
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError
        error={storageError}
        loading={modeProps.mode === "edit" && isStorageLoading}
      >
        {modeProps.mode === "create" ? (
          <BatteryStorageModalForm
            mode="create"
            siteId={siteId}
            variantId={variantId}
            onClose={onClose}
            onSubmit={handleSubmit}
          />
        ) : storage ? (
          <BatteryStorageModalForm
            mode="edit"
            siteId={siteId}
            storage={storage}
            variantId={variantId}
            onClose={onClose}
            onSubmit={handleSubmit}
          />
        ) : null}
      </LoadOrError>
    </Modal>
  );
}

export { BatteryStorageModal, BatteryStorageModalProps };
